import React, { useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import ConfirmDialog from '../../components/ConfirmDialog';
import Drawer from '../../components/Drawer';
import ClientFormFields, { emptyClientForm } from '../../components/ClientFormFields';
import useEditRoute from '../../utils/useEditRoute';
import usePageTitle from '../../utils/usePageTitle';
import { clientsService, clientTypesService } from '../../services/salesService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { formatMasked } from '../../components/ui/masked-input';
import { STATUS_OPTIONS, YES_NO_OPTIONS } from '../../constants/listOptions';
import { brazilianStates } from '../../utils/brazilianLocations';
import useLookup from '../../utils/useLookup';

function ClientsList() {
    usePageTitle('Clientes');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canClients = can('Clients');

    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState(emptyClientForm());

    const clientTypes = useLookup(() => clientTypesService.list());

    const list = useTableList({
        storageKey: 'clients',
        fetcher: (params) => clientsService.list(params),
        errorMessage: 'Não foi possível carregar os clientes',
    });

    const columns = useMemo(() => ([
        { key: 'name', label: 'Nome', className: 'font-medium' },
        { key: 'client_type', label: 'Tipo', render: (value, row) => row.client_type?.name || '-' },
        { key: 'cnpj', label: 'CNPJ/CPF', render: (value) => formatMasked('cpf-cnpj', value) || '-' },
        { key: 'contact_name', label: 'Contato', render: (value) => value || '-' },
        { key: 'phone', label: 'Telefone', render: (value) => formatMasked('phone', value) || '-' },
        { key: 'email', label: 'E-mail', render: (value) => value || '-' },
        {
            key: 'status',
            label: 'Status',
            render: (value) => {
                const map = {
                    active: { label: 'Ativo', className: 'bg-green-100 text-green-800' },
                    inactive: { label: 'Inativo', className: 'bg-gray-100 text-gray-800' },
                };
                const cfg = map[value] || { label: value || '-', className: 'bg-gray-100 text-gray-800' };
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
                        {cfg.label}
                    </span>
                );
            },
        },
    ]), []);

    const openNew = () => {
        setEditingClient(null);
        setFormData(emptyClientForm());
        setDrawerOpen(true);
    };

    const openEdit = async (client) => {
        setFormLoading(true);
        try {
            const response = await clientsService.get(client.id);
            const data = response.data?.data || response.data || client;
            setEditingClient(data);
            setFormData({ ...emptyClientForm(), ...data });
            setDrawerOpen(true);
        } catch (e) {
            console.error('Error loading client:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o cliente' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (client) => setDeleteDialog({ open: true, id: client.id });

    const handleDeleteConfirm = async () => {
        try {
            await clientsService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({ title: 'Cliente excluído', description: 'O cliente foi excluído com sucesso', variant: 'success' });
        } catch (e) {
            console.error('Error deleting client:', e);
            toastApiError(e, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o cliente' });
        }
    };

    const handleFormChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        if (formLoading) return;
        setFormLoading(true);
        try {
            if (editingClient?.id) {
                await clientsService.update(editingClient.id, formData);
                toast({ title: 'Cliente salvo', description: 'Alterações salvas com sucesso', variant: 'success' });
            } else {
                await clientsService.create(formData);
                toast({ title: 'Cliente criado', description: 'Cadastro realizado com sucesso', variant: 'success' });
            }
            setDrawerOpen(false);
            list.reload();
        } catch (e) {
            console.error('Error saving client:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o cliente' });
        } finally {
            setFormLoading(false);
        }
    };

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'client_type_id', label: 'Tipo de cliente', type: 'select', options: clientTypes.map((t) => ({ value: String(t.id), label: t.name })) },
        { key: 'cnpj', label: 'CNPJ/CPF', type: 'text' },
        { key: 'email', label: 'E-mail', type: 'text' },
        { key: 'phone', label: 'Telefone / WhatsApp', type: 'text' },
        { key: 'city', label: 'Cidade', type: 'text' },
        { key: 'state', label: 'UF', type: 'select', options: brazilianStates.map((state) => ({ value: state.code, label: state.code })) },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
        { key: 'has_sites', label: 'Possui obras', type: 'select', options: YES_NO_OPTIONS },
        { key: 'site', label: 'Nome da obra', type: 'text' },
    ];

    useEditRoute({
        basePath: '/settings/clients',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => openEdit({ id }),
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Clientes"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Cadastros' },
                    ]}
                    actions={[
                        ...(canClients ? [{
                            label: 'Novo cliente',
                            icon: 'new',
                            onClick: openNew,
                        }] : []),
                    ]}
                />

                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome, contato, CNPJ, e-mail, telefone ou cidade..."
                    columns={columns}
                    editTo={canClients ? (row) => `/settings/clients/${row.id}` : undefined}
                    onEdit={canClients ? openEdit : undefined}
                    onDelete={canClients ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum cliente cadastrado"
                />
            </div>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir cliente"
                message="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingClient?.id ? 'Editar cliente' : 'Novo cliente'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleSave} disabled={formLoading || !canClients}>
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <ClientFormFields formData={formData} onChange={handleFormChange} />
            </Drawer>
        </AppLayout>
    );
}

export default ClientsList;
